import "./LeaveCalendar.scss";
import "react-datepicker/dist/react-datepicker.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchData, getYears, Notify } from "../utils/constants";
import { Select, Spin, Row, Col, Grid } from "antd";
import StatisticsWidget from "../components/Statistics/Statistics";
import LeaveReqModal from "../components/Modals/LeaveReqModal";
import {
  AnnualPlanItem,
  LeaveRequest,
  BankHolidays,
  User,
} from "../utils/types";
import AnnualLeaveSingleTable from "../components/Tables/AnnualLeaveSingleTable";
import { useUser } from "../context/UserContext";
import RequestsModal from "../components/Modals/RequestsModal";
import AnnualLeaveSingleMobile from "../components/Tables/AnnualLeaveSingleTableMobile";

const years = getYears();

// Every request status that can appear on the personal calendar, bucketed
// under the legend toggle that shows/hides it - see the matching buckets in
// AnnualLeaveSingleTable/AnnualLeaveSingleTableMobile's getCellMeta.
const CALENDAR_VISIBLE_STATUSES = new Set([
  "AWAITING_MANAGER",
  "AWAITING_ADMINISTRATIV",
  "RETURNED_FOR_CORRECTION",
  "AWAITING_DOCUMENT_REVIEW",
  "APPROVED",
  "REJECTED",
  "CANCELLED",
  "CANCELLATION_REJECTED",
  "AWAITING_MANAGER_CANCELLATION",
  "AWAITING_ADMINISTRATIV_CANCELLATION",
]);

const MyCalendar = () => {
  const { authUser } = useUser();

  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [bankHolidays, setBankHolidays] = useState<BankHolidays[]>([]);
  const [annualPlan, setAnnualPlan] = useState<AnnualPlanItem[]>([]);
  const [annualLeaveYear, setAnnualLeaveYear] = useState(years[1]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedStartDate, setSelectedStartDate] = useState<Date>(new Date());
  const [selectedLeaveTypes, setSelectedLeaveTypes] = useState<string[]>([
    "PENDING",
    "APPROVED",
    "REJECTED",
    "CANCELLED",
    "PENDING_CANCELLED",
    "BANK_HOLIDAY",
    "ANNUAL_PLAN",
  ]);

  const [selfUser, setSelfUser] = useState<User | null>(null);
  const [isStatisticsModalOpen, setIsStatisticsModalOpen] =
    useState<boolean>(false);
  const [statisticModalType, setStatisticModalType] = useState<
    "Approved" | "Rejected" | "Cancelled" | "Pending" | "PendingCancelled"
  >("Approved");
  const [loading, setLoading] = useState<boolean>(false);
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      await fetchData(
        `${process.env.REACT_APP_API_URL}/users/getAllUsers`,
        (data: User[]) => {
          const found = data.find((u: User) => u.email === authUser?.email);
          if (found) {
            setSelfUser(found);
          }
        },
      );
    } catch (error) {
      Notify("error", "Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  }, [authUser]);

  useEffect(() => {
    if (selfUser && leaveRequests.length === 0) {
      setLeaveRequests([
        {
          id: 0,
          employeeName: `${selfUser.firstName} ${selfUser.lastName}`,
          startDate: "",
          endDate: "",
          requestStatus: "",
          requestType: "",
          leaveDays: [],
          comment: "",
          substituteName: "",
          currentYearRemainingDays: selfUser.currentYearDays,
        },
      ]);
    }
  }, [leaveRequests, selfUser]);

  const fetchSelfData = useCallback(async () => {
    const employeeId = selfUser?.id;
    if (typeof employeeId !== "number") return;
    try {
      setLoading(true);
      await Promise.all([
        fetchData(
          `${process.env.REACT_APP_API_URL}/getRequestsByEmployeeId/${employeeId}`,
          (data: LeaveRequest[]) => {
            if (data.length) setLeaveRequests(data);
          },
        ),
        fetchData(
          `${process.env.REACT_APP_API_URL}/holidays/getAllHolidays`,
          setBankHolidays,
        ),
        fetchData(
          `${process.env.REACT_APP_API_URL}/leave-planning/getLeaveSchedules`,
          setAnnualPlan,
        ),
      ]);
      setIsDataLoaded(true);
    } catch (error) {
      Notify("error", "Failed to fetch self data.");
    } finally {
      setLoading(false);
    }
  }, [selfUser]);

  const fetchReqById = useCallback(async () => {
    const employeeId = selfUser?.id;
    if (typeof employeeId !== "number") return;
    try {
      setLoading(true);
      await fetchData(
        `${process.env.REACT_APP_API_URL}/getRequestsByEmployeeId/${employeeId}`,
        setLeaveRequests,
      );
      setIsDataLoaded(true);
    } catch (error) {
      console.error("Error while fetching user requests:", error);
      Notify("error", "Failed to fetch user requests.");
    } finally {
      setLoading(false);
    }
  }, [selfUser]);

  const filteredLeaveRequests = useMemo(() => {
    // Every status the calendar's legend toggles (Pending/Approved/
    // Rejected/Cancelled/Pending Cancelled) know how to bucket and display -
    // see CALENDAR_VISIBLE_STATUSES. DRAFT/MANAGER_REVIEW never reach a real
    // employee's calendar and are intentionally excluded.
    return leaveRequests.filter((req) =>
      CALENDAR_VISIBLE_STATUSES.has(req.requestStatus),
    );
  }, [leaveRequests]);

  const groupByRequestStatus = (leaveRequests: LeaveRequest[]) => {
    return leaveRequests.reduce((acc: Record<string, LeaveRequest[]>, request: LeaveRequest) => {
      const status = request.requestStatus;
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(request);
      return acc;
    }, {} as Record<string, LeaveRequest[]>);
  };

  const groupedRequests = useMemo(
    () => groupByRequestStatus(leaveRequests),
    [leaveRequests],
  );

  const statisticsItemsSingle = [
    { key: "year", label: "Year", value: annualLeaveYear.value },
    {
      key: "pendingRequests",
      label: "Pending",
      value: groupedRequests.AWAITING_MANAGER?.length || 0,
      onClick: () => {
        setStatisticModalType("Pending");
        setIsStatisticsModalOpen(true);
      },
    },
    {
      key: "approvedRequests",
      label: "Approved",
      value: groupedRequests.APPROVED?.length || 0,
      onClick: () => {
        setStatisticModalType("Approved");
        setIsStatisticsModalOpen(true);
      },
    },
    {
      key: "rejectedRequests",
      label: "Rejected",
      value: groupedRequests.REJECTED?.length || 0,
      onClick: () => {
        setStatisticModalType("Rejected");
        setIsStatisticsModalOpen(true);
      },
    },
    {
      key: "cancelledRequests",
      label: "Cancelled",
      value: groupedRequests.CANCELLED?.length || 0,
      onClick: () => {
        setStatisticModalType("Cancelled");
        setIsStatisticsModalOpen(true);
      },
    },
    {
      key: "pendingCancelledRequests",
      label: "Pending Cancelled",
      value: groupedRequests.AWAITING_MANAGER_CANCELLATION?.length || 0,
      onClick: () => {
        setStatisticModalType("PendingCancelled");
        setIsStatisticsModalOpen(true);
      },
    },
  ];

  const handleOpenLeaveModal = useCallback((user: User | null, date: Date) => {
    if (user) {
      setSelectedStartDate(date);
      setIsOpen(true);
    }
  }, []);

  const toggleLeaveType = useCallback((type: string) => {
    setSelectedLeaveTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  }, []);

  const handleYearChange = useCallback((e: unknown, data: { value: string }) => {
    const selectedYear = years.find((y) => y.value === data.value);
    if (selectedYear) {
      setAnnualLeaveYear(selectedYear);
    }
  }, []);

  useEffect(() => {
    if (!authUser) return;
    fetchUsers();
  }, [authUser, fetchUsers]);

  useEffect(() => {
    if (selfUser) {
      fetchSelfData();
    }
  }, [selfUser, fetchSelfData, annualLeaveYear]);

  const screens = Grid.useBreakpoint() || {};
  const isMobile = !screens.md;
  return (
    <div className="leave-calendar-wrapper">
      <Row className="my-calendar-header" gutter={[12, 12]} align="middle">
        <Col xs={24} xl={18}>
          <Row gutter={[12, 12]} align="middle" wrap>
            <Col xs={24} sm="auto">
              <span className="header-label">Select a year:</span>
            </Col>

            <Col xs={24} sm={6} md={4}>
              <Select
                className="year-select"
                value={annualLeaveYear.value}
                options={years}
                onChange={(_, option) =>
                  handleYearChange(null, option as { value: string })
                }
                style={{ width: "100%" }}
              />
            </Col>

            <Col xs={24} sm={18} md={14} className="stats-container">
              {loading ? (
                <div className="stats-loading">
                  <Spin size="small" />
                  <span>Loading statistics...</span>
                </div>
              ) : (
                <StatisticsWidget statisticsItems={statisticsItemsSingle} />
              )}
            </Col>
          </Row>
        </Col>
      </Row>

      <LeaveReqModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        selectedStartDate={selectedStartDate}
        refreshData={fetchReqById}
        withButton={false}
      />

      {isMobile ? (
        <AnnualLeaveSingleMobile
          annualLeaveYear={annualLeaveYear.value}
          leaveRequests={filteredLeaveRequests}
          user={selfUser}
          onDateClick={handleOpenLeaveModal}
          selectedLeaveTypes={selectedLeaveTypes}
          bankHolidays={bankHolidays}
          annualPlan={annualPlan}
        />
      ) : (
        <AnnualLeaveSingleTable
          annualLeaveYear={annualLeaveYear.value}
          leaveRequests={filteredLeaveRequests}
          user={selfUser}
          onDateClick={handleOpenLeaveModal}
          selectedLeaveTypes={selectedLeaveTypes}
          bankHolidays={bankHolidays}
          annualPlan={annualPlan}
          loading={!isDataLoaded && !selfUser}
        />
      )}

      <RequestsModal
        isOpen={isStatisticsModalOpen}
        setIsOpen={setIsStatisticsModalOpen}
        type={statisticModalType}
        userId={selfUser?.id}
      />

      <div className="leave-legend">
        {[
          "PENDING",
          "APPROVED",
          "REJECTED",
          "CANCELLED",
          "PENDING_CANCELLED",
          "BANK_HOLIDAY",
          "ANNUAL_PLAN",
        ].map((type) => {
          const formatted = type
            .toLowerCase()
            .replace(/_/g, " ")
            .replace(/\b\w/g, (char) => char.toUpperCase());

          return (
            <span
              key={type}
              className={`leave-${type} legend-item ${
                selectedLeaveTypes.includes(type) ? "active" : "inactive"
              }`}
              onClick={() => toggleLeaveType(type)}
            >
              {formatted}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default MyCalendar;
